import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { getLevelInfo, getGrade } from '../utils/xpUtils';
import Icon from '../components/Icon';

const GAMES = [
  { key: 'lightspeed', label: 'LightSpeed', path: '/lightspeed', xp: 1000 },
  { key: 'cirquiz',    label: 'CirQuiz',    path: '/quiz',        xp: 500  },
  { key: 'podiumquiz', label: 'PodiumQuiz', path: '/podium',      xp: 500  },
];

function copyText(t) { navigator.clipboard?.writeText(t).catch(() => {}); }

const Av = ({ u, size = 32, color = '#e10600' }) => (
  <div style={{ width: size, height: size, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, border: `1.5px solid ${color}55` }}>
    {u?.avatar
      ? <img src={u.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      : <div style={{ width: '100%', height: '100%', background: color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: size * 0.38, color }}>
          {u?.username?.[0]?.toUpperCase() ?? '?'}
        </div>
    }
  </div>
);

/* ── Chat ──────────────────────────────────────────────────────────────────── */
const ChatTab = ({ ecurie, user }) => {
  const [messages, setMessages] = useState([]);
  const [text, setText]         = useState('');
  const [sending, setSending]   = useState(false);
  const endRef  = useRef(null);
  const pollRef = useRef(null);
  const color   = ecurie.color ?? '#e10600';

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/ecuries/${ecurie._id}/messages`);
      setMessages(r.data);
    } catch {}
  }, [ecurie._id]);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 5000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/ecuries/${ecurie._id}/messages`, { text: text.trim() });
      setText('');
      await load();
    } catch {} finally { setSending(false); }
  };

  const myId = String(user?.id ?? user?._id ?? '');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '60vh', minHeight: 400 }}>
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {messages.length === 0 && (
          <p style={{ textAlign: 'center', color: '#52525b', fontSize: 13, padding: '40px 0' }}>
            Aucun message encore. Lancez la discussion !
          </p>
        )}
        {messages.map(m => {
          const isMe = String(m.author?._id) === myId;
          return (
            <div key={m._id} style={{ display: 'flex', flexDirection: isMe ? 'row-reverse' : 'row', alignItems: 'flex-end', gap: 8 }}>
              {!isMe && <Av u={m.author} size={28} color={color} />}
              <div style={{ maxWidth: '72%' }}>
                {!isMe && (
                  <p style={{ fontSize: 9, color: '#71717a', marginBottom: 3, marginLeft: 4, fontWeight: 700 }}>
                    {m.author?.username}
                  </p>
                )}
                <div style={{
                  padding: '8px 12px', borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  background: isMe ? color : 'rgba(255,255,255,.08)',
                  color: isMe ? (color === '#EAB308' || color === '#facc15' ? '#000' : '#fff') : '#e4e4e7',
                  fontSize: 13, lineHeight: 1.4,
                }}>
                  {m.text}
                </div>
                <p style={{ fontSize: 8, color: '#52525b', marginTop: 2, textAlign: isMe ? 'right' : 'left', marginLeft: isMe ? 0 : 4, marginRight: isMe ? 4 : 0 }}>
                  {new Date(m.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <form onSubmit={send} style={{ display: 'flex', gap: 8, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,.07)' }}>
        <input
          value={text} onChange={e => setText(e.target.value)}
          placeholder="Écrire un message…" maxLength={500}
          style={{
            flex: 1, background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.1)',
            borderRadius: 12, padding: '10px 14px', color: '#fff', fontSize: 13, outline: 'none',
          }}
          onFocus={e => { e.target.style.borderColor = color; }}
          onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,.1)'; }}
        />
        <button type="submit" disabled={!text.trim() || sending}
          style={{
            padding: '10px 18px', borderRadius: 12, fontWeight: 800, fontSize: 13, cursor: 'pointer',
            background: color, color: '#fff', border: 'none', opacity: (!text.trim() || sending) ? 0.4 : 1,
          }}>
          {sending ? '…' : '↑'}
        </button>
      </form>
    </div>
  );
};

/* ── Défis ─────────────────────────────────────────────────────────────────── */
const DefisTab = ({ ecurie, user }) => {
  const navigate = useNavigate();
  const [gameModal, setGameModal] = useState(null);
  const [sent, setSent]           = useState({});
  const [loadingId, setLoadingId] = useState(null);
  const color = ecurie.color ?? '#e10600';

  const members = (ecurie.members ?? []).filter(m => {
    const uid = m.user?._id ?? m.user;
    return String(uid) !== String(user?.id ?? user?._id ?? '');
  });

  const sendChallenge = async (memberId, game) => {
    setLoadingId(memberId + game);
    try {
      await api.post('/challenges', { userId: memberId, game });
      setSent(prev => ({ ...prev, [memberId + game]: true }));
    } catch {}
    finally { setLoadingId(null); }
  };

  if (members.length === 0) return (
    <p style={{ textAlign: 'center', color: '#52525b', fontSize: 13, padding: '40px 0' }}>
      Personne d'autre dans l'écurie pour l'instant.
    </p>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <p style={{ fontSize: 9, color: '#52525b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 6 }}>
        Défier un membre · Score hors classement global
      </p>
      {members.map(m => {
        const u   = m.user ?? {};
        const uid = String(u._id ?? u);
        return (
          <div key={uid} style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '10px 14px', borderRadius: 16,
            border: '1px solid rgba(255,255,255,.08)', background: 'rgba(255,255,255,.03)',
          }}>
            <Link to={`/joueurs/${uid}`}><Av u={u} size={36} color={color} /></Link>
            <div style={{ flex: 1 }}>
              <Link to={`/joueurs/${uid}`} style={{ color: '#fff', fontWeight: 700, fontSize: 13, textDecoration: 'none' }}>
                {u.username ?? '—'}
              </Link>
              <p style={{ fontSize: 10, color: '#71717a', margin: '2px 0 0' }}>
                {m.ecurieScore ?? 0} pts écurie
              </p>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {GAMES.map(g => {
                const key    = uid + g.key;
                const isSent = sent[key];
                const isLoad = loadingId === key;
                return (
                  <button key={g.key} onClick={() => !isSent && sendChallenge(uid, g.key)}
                    disabled={isSent || isLoad}
                    title={g.label}
                    style={{
                      padding: '5px 10px', borderRadius: 10, fontSize: 11, fontWeight: 800, cursor: 'pointer',
                      border: `1px solid ${isSent ? '#22c55e55' : color + '44'}`,
                      background: isSent ? 'rgba(34,197,94,.1)' : color + '18',
                      color: isSent ? '#22c55e' : color,
                      opacity: isLoad ? 0.5 : 1,
                    }}>
                    {isSent ? '✓' : isLoad ? '…' : g.label.split(' ')[1] ?? g.label[0]}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
      <div style={{ marginTop: 16, padding: '12px 14px', borderRadius: 14, background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.06)' }}>
        <p style={{ fontSize: 11, color: '#71717a', lineHeight: 1.5 }}>
          Les défis envoyés apparaissent dans l'onglet <strong style={{ color: '#a1a1aa' }}>Défis</strong> de la page Amis. Lance un jeu pour y répondre !
        </p>
      </div>
    </div>
  );
};

/* ── Paris ─────────────────────────────────────────────────────────────────── */
const CreateBetForm = ({ ecurie, onCreated, onCancel }) => {
  const [question, setQuestion] = useState('');
  const [options,  setOptions]  = useState(['', '']);
  const [xpStake,  setXpStake]  = useState(100);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const color = ecurie.color ?? '#e10600';

  const updateOption = (i, v) => setOptions(prev => { const n = [...prev]; n[i] = v; return n; });
  const addOption    = () => options.length < 6 && setOptions(prev => [...prev, '']);
  const removeOption = (i) => options.length > 2 && setOptions(prev => prev.filter((_, j) => j !== i));

  const submit = async (e) => {
    e.preventDefault();
    const opts = options.map(o => o.trim()).filter(Boolean);
    if (!question.trim() || opts.length < 2) { setError('Question et au moins 2 options requises.'); return; }
    setLoading(true); setError('');
    try {
      const r = await api.post(`/ecuries/${ecurie._id}/bets`, { question: question.trim(), options: opts, xpStake });
      onCreated(r.data);
    } catch (err) { setError(err?.response?.data?.message || 'Erreur'); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14,
      padding: '16px', borderRadius: 16, border: `1px solid ${color}44`, background: color + '0a', marginBottom: 16 }}>
      <p style={{ fontSize: 11, fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: 1.5 }}>Nouveau pari</p>
      <div>
        <p style={{ fontSize: 9, color: '#71717a', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 5 }}>Question</p>
        <input value={question} onChange={e => setQuestion(e.target.value)} maxLength={200}
          placeholder="ex: Qui sera sur le podium dimanche ?"
          style={{ width: '100%', background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 10, padding: '8px 12px', color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
          onFocus={e => { e.target.style.borderColor = color; }} onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,.1)'; }} />
      </div>
      <div>
        <p style={{ fontSize: 9, color: '#71717a', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 5 }}>Options</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {options.map((o, i) => (
            <div key={i} style={{ display: 'flex', gap: 6 }}>
              <input value={o} onChange={e => updateOption(i, e.target.value)} maxLength={60}
                placeholder={`Option ${i + 1}`}
                style={{ flex: 1, background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 8, padding: '7px 10px', color: '#fff', fontSize: 12, outline: 'none' }}
                onFocus={e => { e.target.style.borderColor = color; }} onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,.08)'; }} />
              {options.length > 2 && (
                <button type="button" onClick={() => removeOption(i)}
                  style={{ width: 28, borderRadius: 8, background: 'rgba(225,6,0,.12)', border: 'none', color: '#e10600', cursor: 'pointer', fontSize: 13, fontWeight: 900 }}>✕</button>
              )}
            </div>
          ))}
          {options.length < 6 && (
            <button type="button" onClick={addOption}
              style={{ alignSelf: 'flex-start', fontSize: 11, color: '#71717a', background: 'none', border: '1px dashed rgba(255,255,255,.12)', borderRadius: 8, padding: '5px 12px', cursor: 'pointer' }}>
              + Ajouter une option
            </button>
          )}
        </div>
      </div>
      <div>
        <p style={{ fontSize: 9, color: '#71717a', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 5 }}>Mise ({xpStake} pts écurie)</p>
        <input type="range" min={50} max={500} step={50} value={xpStake} onChange={e => setXpStake(Number(e.target.value))}
          style={{ width: '100%', accentColor: color }} />
        <p style={{ fontSize: 10, color: '#71717a', marginTop: 3 }}>Gagnant remporte {xpStake * 2} pts écurie</p>
      </div>
      {error && <p style={{ fontSize: 11, color: '#e10600' }}>{error}</p>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" disabled={loading}
          style={{ flex: 1, padding: '9px 0', borderRadius: 10, background: color, color: '#fff', fontWeight: 800, fontSize: 13, border: 'none', cursor: 'pointer', opacity: loading ? 0.5 : 1 }}>
          {loading ? '…' : 'Créer le pari'}
        </button>
        <button type="button" onClick={onCancel}
          style={{ padding: '9px 16px', borderRadius: 10, background: 'rgba(255,255,255,.06)', color: '#a1a1aa', fontWeight: 700, fontSize: 13, border: '1px solid rgba(255,255,255,.1)', cursor: 'pointer' }}>
          Annuler
        </button>
      </div>
    </form>
  );
};

const BetCard = ({ bet, ecurie, user, onUpdated }) => {
  const [loading, setLoading] = useState(false);
  const color   = ecurie.color ?? '#e10600';
  const myId    = String(user?.id ?? user?._id ?? '');
  const myVote  = bet.votes?.find(v => String(v.user?._id ?? v.user) === myId)?.choice;
  const isOwner = String(ecurie.owner?._id ?? ecurie.owner) === myId;
  const total   = bet.votes?.length ?? 0;

  const vote = async (choice) => {
    if (bet.status !== 'open' || loading) return;
    setLoading(true);
    try {
      const r = await api.post(`/ecuries/${ecurie._id}/bets/${bet._id}/vote`, { choice });
      onUpdated(r.data);
    } catch {} finally { setLoading(false); }
  };

  const resolve = async (result) => {
    if (loading) return;
    setLoading(true);
    try {
      const r = await api.post(`/ecuries/${ecurie._id}/bets/${bet._id}/resolve`, { result });
      onUpdated(r.data);
    } catch {} finally { setLoading(false); }
  };

  const countFor = (opt) => (bet.votes ?? []).filter(v => v.choice === opt).length;
  const pct      = (opt) => total === 0 ? 0 : Math.round((countFor(opt) / total) * 100);

  return (
    <div style={{ padding: '14px', borderRadius: 16,
      border: `1px solid ${bet.status === 'resolved' ? 'rgba(255,255,255,.1)' : color + '44'}`,
      background: bet.status === 'resolved' ? 'rgba(255,255,255,.02)' : color + '08',
      marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
        <p style={{ fontSize: 14, fontWeight: 800, color: '#e4e4e7', flex: 1, lineHeight: 1.3 }}>{bet.question}</p>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, flexShrink: 0 }}>
          <span style={{
            fontSize: 9, fontWeight: 800, padding: '2px 8px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: 1,
            background: bet.status === 'resolved' ? 'rgba(255,255,255,.08)' : color + '22',
            color: bet.status === 'resolved' ? '#71717a' : color,
          }}>
            {bet.status === 'resolved' ? 'Résolu' : 'Ouvert'}
          </span>
          <span style={{ fontSize: 9, color: '#52525b' }}>Mise: {bet.xpStake} pts · {total} vote{total !== 1 ? 's' : ''}</span>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {bet.options.map(opt => {
          const isWinner  = bet.status === 'resolved' && bet.result === opt;
          const isMyChoice = myVote === opt;
          const p = pct(opt);
          return (
            <div key={opt}>
              <button onClick={() => vote(opt)} disabled={bet.status !== 'open' || loading}
                style={{
                  width: '100%', textAlign: 'left', padding: '8px 12px', borderRadius: 10, cursor: bet.status === 'open' ? 'pointer' : 'default',
                  border: `1.5px solid ${isWinner ? '#22c55e55' : isMyChoice ? color + '88' : 'rgba(255,255,255,.08)'}`,
                  background: isWinner ? 'rgba(34,197,94,.1)' : isMyChoice ? color + '18' : 'rgba(255,255,255,.04)',
                  position: 'relative', overflow: 'hidden',
                }}>
                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${p}%`, background: isWinner ? 'rgba(34,197,94,.15)' : color + '12', transition: 'width .5s ease', zIndex: 0 }} />
                <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, fontWeight: isMyChoice || isWinner ? 800 : 500, color: isWinner ? '#22c55e' : isMyChoice ? color : '#a1a1aa' }}>
                    {isWinner && <Icon name="trophy" size={11} style={{ display:'inline', verticalAlign:'middle', marginRight:3 }} />}{opt}{isMyChoice && !isWinner && ' ✓'}
                  </span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#71717a' }}>{p}% ({countFor(opt)})</span>
                </div>
              </button>
            </div>
          );
        })}
      </div>

      {isOwner && bet.status === 'open' && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,.07)' }}>
          <p style={{ fontSize: 9, color: '#71717a', marginBottom: 6, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Résoudre (fondateur)</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {bet.options.map(opt => (
              <button key={opt} onClick={() => resolve(opt)} disabled={loading}
                style={{ fontSize: 10, padding: '4px 10px', borderRadius: 8, cursor: 'pointer', fontWeight: 700,
                  background: '#22c55e18', border: '1px solid #22c55e44', color: '#22c55e' }}>
                ✓ {opt}
              </button>
            ))}
          </div>
        </div>
      )}
      <p style={{ fontSize: 9, color: '#3f3f46', marginTop: 8 }}>
        par {bet.author?.username} · {new Date(bet.createdAt).toLocaleDateString('fr-FR')}
      </p>
    </div>
  );
};

const ParisTab = ({ ecurie, user }) => {
  const [bets, setBets]         = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading]   = useState(true);
  const color = ecurie.color ?? '#e10600';

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/ecuries/${ecurie._id}/bets`);
      setBets(r.data);
    } catch {} finally { setLoading(false); }
  }, [ecurie._id]);

  useEffect(() => { load(); }, [load]);

  const isMember = (ecurie.members ?? []).some(m => {
    const uid = m.user?._id ?? m.user;
    return String(uid) === String(user?.id ?? user?._id ?? '');
  });

  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><div className="animate-spin w-6 h-6 border-2 rounded-full mx-auto" style={{ borderColor: `${color} transparent transparent transparent` }} /></div>;

  return (
    <div>
      {isMember && (
        showCreate
          ? <CreateBetForm ecurie={ecurie} onCreated={b => { setBets(prev => [b, ...prev]); setShowCreate(false); }} onCancel={() => setShowCreate(false)} />
          : <button onClick={() => setShowCreate(true)} style={{
              width: '100%', marginBottom: 16, padding: '10px 0', borderRadius: 12, fontWeight: 800, fontSize: 13, cursor: 'pointer',
              background: color + '18', border: `1px dashed ${color}66`, color }}>
              Créer un pari
            </button>
      )}

      {bets.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#52525b', fontSize: 13, padding: '40px 0' }}>
          Aucun pari en cours. Crée le premier !
        </p>
      ) : (
        bets.map(b => (
          <BetCard key={b._id} bet={b} ecurie={ecurie} user={user}
            onUpdated={updated => setBets(prev => prev.map(x => x._id === updated._id ? updated : x))} />
        ))
      )}
    </div>
  );
};

/* ── Classement ────────────────────────────────────────────────────────────── */
const ClassementTab = ({ ecurie }) => {
  const color = ecurie.color ?? '#e10600';
  const sorted = [...(ecurie.members ?? [])].sort((a, b) => (b.ecurieScore ?? 0) - (a.ecurieScore ?? 0));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <p style={{ fontSize: 9, color: '#52525b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 6 }}>
        Points d'écurie (paris + défis) · hors classement global
      </p>
      {sorted.map((m, i) => {
        const u = m.user ?? {};
        const { level } = getLevelInfo(u.xp ?? 0);
        const g = getGrade(level);
        return (
          <Link key={u._id ?? i} to={`/joueurs/${u._id}`}
            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 14, textDecoration: 'none',
              border: `1px solid ${i === 0 ? color + '44' : 'rgba(255,255,255,.07)'}`,
              background: i === 0 ? color + '0c' : 'rgba(255,255,255,.02)' }}>
            <span style={{ fontSize: 14, width: 22, textAlign: 'center', fontWeight: 900,
              color: i === 0 ? '#F59E0B' : i === 1 ? '#94A3B8' : i === 2 ? '#B45309' : '#52525b' }}>
              {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
            </span>
            <Av u={u} size={34} color={color} />
            <div style={{ flex: 1 }}>
              <p style={{ color: '#fff', fontWeight: 700, fontSize: 13, margin: 0 }}>{u.username}</p>
              <span style={{ fontSize: 9, fontWeight: 800, padding: '1px 6px', borderRadius: 4, background: g.bg, color: g.color }}>
                {g.label}
              </span>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ color: '#fff', fontWeight: 900, fontSize: 16, margin: 0 }}>{m.ecurieScore ?? 0}</p>
              <p style={{ color: '#71717a', fontSize: 9, margin: 0 }}>pts écurie</p>
            </div>
          </Link>
        );
      })}
    </div>
  );
};

/* ── Accueil ───────────────────────────────────────────────────────────────── */
const AccueilTab = ({ ecurie, user, onJoin, onLeave, onDelete, actLoading }) => {
  const [copied, setCopied] = useState(false);
  const color   = ecurie.color ?? '#e10600';
  const myId    = String(user?.id ?? user?._id ?? '');
  const isMember = (ecurie.members ?? []).some(m => String(m.user?._id ?? m.user) === myId);
  const isOwner  = String(ecurie.owner?._id ?? ecurie.owner) === myId;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {ecurie.description && (
        <div style={{ padding: '12px 14px', borderRadius: 14, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.07)' }}>
          <p style={{ color: '#a1a1aa', fontSize: 13, lineHeight: 1.5, margin: 0 }}>{ecurie.description}</p>
        </div>
      )}

      <div style={{ padding: '10px 14px', borderRadius: 14, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.07)' }}>
        <p style={{ fontSize: 9, color: '#52525b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 6 }}>Code d'invitation</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontFamily: 'monospace', fontWeight: 900, fontSize: 20, letterSpacing: '0.3em', color: '#fff' }}>{ecurie.inviteCode}</span>
          <button onClick={() => { copyText(ecurie.inviteCode); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
            style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 8, cursor: 'pointer', border: `1px solid ${color}55`, background: color + '18', color }}>
            {copied ? '✓ Copié' : 'Copier'}
          </button>
        </div>
      </div>

      <div>
        <p style={{ fontSize: 9, color: '#52525b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8 }}>
          {ecurie.members?.length ?? 0} membre{(ecurie.members?.length ?? 0) !== 1 ? 's' : ''}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {(ecurie.members ?? []).map((m, i) => {
            const u = m.user ?? {};
            const isOwnerRow = String(u._id ?? u) === String(ecurie.owner?._id ?? ecurie.owner);
            return (
              <Link key={u._id ?? i} to={`/joueurs/${u._id}`}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 12, textDecoration: 'none',
                  border: '1px solid rgba(255,255,255,.07)', background: 'rgba(255,255,255,.025)' }}>
                <Av u={u} size={30} color={color} />
                <p style={{ flex: 1, color: '#d4d4d8', fontSize: 12, fontWeight: 600, margin: 0 }}>{u.username}</p>
                {isOwnerRow && <Icon name="star" size={11} />}
                <span style={{ fontSize: 10, color: '#52525b' }}>{m.ecurieScore ?? 0} pts</span>
              </Link>
            );
          })}
        </div>
      </div>

      {user && (
        <div style={{ display: 'flex', gap: 8, paddingTop: 8 }}>
          {!isMember && !isOwner && (
            <button onClick={onJoin} disabled={actLoading}
              style={{ flex: 1, padding: '11px 0', borderRadius: 12, fontWeight: 800, fontSize: 14, cursor: 'pointer', border: 'none',
                background: color, color: '#fff', opacity: actLoading ? 0.5 : 1 }}>
              {actLoading ? '…' : "Rejoindre l'écurie"}
            </button>
          )}
          {isMember && !isOwner && (
            <button onClick={onLeave} disabled={actLoading}
              style={{ flex: 1, padding: '11px 0', borderRadius: 12, fontWeight: 700, fontSize: 13, cursor: 'pointer',
                background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.12)', color: '#a1a1aa', opacity: actLoading ? 0.5 : 1 }}>
              {actLoading ? '…' : "Quitter l'écurie"}
            </button>
          )}
          {isOwner && (
            <button onClick={onDelete} disabled={actLoading}
              style={{ flex: 1, padding: '11px 0', borderRadius: 12, fontWeight: 700, fontSize: 13, cursor: 'pointer',
                background: 'rgba(225,6,0,.08)', border: '1px solid rgba(225,6,0,.25)', color: '#e10600', opacity: actLoading ? 0.5 : 1 }}>
              {actLoading ? '…' : '🗑 Supprimer l\'écurie'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/* ── Main page ─────────────────────────────────────────────────────────────── */
const EcurieSalon = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ecurie, setEcurie]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState('accueil');
  const [actLoading, setActLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/ecuries/${id}`);
      setEcurie(r.data);
    } catch {} finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleJoin = async () => {
    setActLoading(true);
    try { await api.post(`/ecuries/${id}/join`); await load(); } catch {}
    finally { setActLoading(false); }
  };

  const handleLeave = async () => {
    setActLoading(true);
    try { await api.post(`/ecuries/${id}/leave`); navigate('/ecuries'); } catch {}
    finally { setActLoading(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer définitivement cette écurie ?')) return;
    setActLoading(true);
    try { await api.delete(`/ecuries/${id}`); navigate('/ecuries'); } catch {}
    finally { setActLoading(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="w-8 h-8 border-2 border-f1red border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!ecurie) return (
    <div className="max-w-2xl mx-auto px-4 py-20 text-center">
      <p className="text-zinc-500 mb-4">Écurie introuvable.</p>
      <Link to="/ecuries" className="text-f1red hover:underline">← Retour aux écuries</Link>
    </div>
  );

  const color = ecurie.color ?? '#e10600';
  const myId  = String(user?.id ?? user?._id ?? '');
  const isMember = (ecurie.members ?? []).some(m => String(m.user?._id ?? m.user) === myId);

  const TABS = [
    { key: 'accueil',    label: 'Accueil',     icon: 'home',    always: true },
    { key: 'chat',       label: 'Chat',        icon: 'message', members: true },
    { key: 'defis',      label: 'Défis',       icon: 'sword',   members: true },
    { key: 'paris',      label: 'Paris',       icon: 'star',    members: true },
    { key: 'classement', label: 'Classement',  icon: 'podium',  always: true },
  ].filter(t => t.always || (t.members && isMember));

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Back */}
      <Link to="/ecuries" style={{ fontSize: 12, color: '#71717a', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5, marginBottom: 20 }}>
        ← Toutes les écuries
      </Link>

      {/* Header */}
      <div style={{
        borderRadius: 20, overflow: 'hidden', marginBottom: 24,
        border: `2px solid ${color}55`,
        background: `linear-gradient(135deg,${color}15 0%,#09090b 60%)`,
        boxShadow: `0 0 40px ${color}15`,
      }}>
        <div style={{ height: 4, background: `linear-gradient(90deg,${color} 0%,transparent 75%)` }} />
        <div style={{ padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, flexShrink: 0, background: color + '22', border: `2px solid ${color}50` }}>
            {ecurie.emoji}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h1 style={{ color: '#fff', fontWeight: 900, fontSize: 22, margin: 0 }}>{ecurie.name}</h1>
              <span style={{ fontSize: 10, fontWeight: 900, padding: '2px 7px', borderRadius: 6, fontFamily: 'monospace', background: color + '22', border: `1px solid ${color}55`, color }}>
                [{ecurie.tag}]
              </span>
              {isMember && <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 20, background: '#22c55e18', border: '1px solid #22c55e44', color: '#22c55e' }}>Membre</span>}
            </div>
            <p style={{ color: '#71717a', fontSize: 12, margin: '4px 0 0' }}>
              {ecurie.members?.length ?? 0} membre{(ecurie.members?.length ?? 0) !== 1 ? 's' : ''} · fondé par {ecurie.owner?.username}
            </p>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, background: 'rgba(255,255,255,.04)', borderRadius: 14, padding: 4, marginBottom: 20, flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              flex: 1, minWidth: 70, padding: '8px 6px', borderRadius: 10, fontSize: 10, fontWeight: tab === t.key ? 800 : 600, cursor: 'pointer',
              background: tab === t.key ? color + '22' : 'transparent',
              border: tab === t.key ? `1px solid ${color}55` : '1px solid transparent',
              color: tab === t.key ? color : '#71717a',
              transition: 'all .2s', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
            }}>
            <Icon name={t.icon} size={14} className={tab === t.key ? '' : 'text-zinc-600'} style={{ color: tab === t.key ? color : undefined }} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ minHeight: 300 }}>
        {tab === 'accueil'    && <AccueilTab    ecurie={ecurie} user={user} onJoin={handleJoin} onLeave={handleLeave} onDelete={handleDelete} actLoading={actLoading} />}
        {tab === 'chat'       && <ChatTab       ecurie={ecurie} user={user} />}
        {tab === 'defis'      && <DefisTab      ecurie={ecurie} user={user} />}
        {tab === 'paris'      && <ParisTab      ecurie={ecurie} user={user} />}
        {tab === 'classement' && <ClassementTab ecurie={ecurie} />}
      </div>
    </div>
  );
};

export default EcurieSalon;
